import { graphql } from '~/gql'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { gqlClient } from '~/hooks/useGraphQL.server'
import {
  CreateWorkspaceInput,
  WorkspacListQuery,
  WorkspaceDashboardQuery,
} from '~/gql/graphql'
import { useMemo } from 'react'
import { set } from 'lodash/fp'

const createWorkspaceMutation = graphql(`
  mutation createWorkspaceMutation($input: CreateWorkspaceInput!) {
    createWorkspace(input: $input) {
      id
    }
  }
`)

function throwStandardError(e: any) {
  // Extract GraphQL error messages
  const messages =
    e?.response?.errors?.map((e: any) => e.message).join(', ') ||
    'Something went wrong while creating your new workspace.'

  // Wrap in a standard Error object
  throw new Error(messages)
}

export const useCreateWorkspaceMutation = () =>
  useMutation({
    mutationFn: async (input: CreateWorkspaceInput) => {
      try {
        const response = await gqlClient().request(createWorkspaceMutation, {
          input,
        })
        return response
      } catch (e) {
        throwStandardError(e)
      }
    },
  })

const updateWorkspaceMutation = graphql(`
  mutation UpdateWorkspace($input: CreateWorkspaceInput!) {
    updateWorkspace(input: $input) {
      commonName
      parentId
      billingCode
    }
  }
`)

export const useUpdateWorkspaceMutation = (memberId: string) => {
  const queryClient = useQueryClient()
  const dashboardWorkspaceQueryKey = useMemo(
    () => ['WorkspaceDashboard', undefined] as const,
    [memberId]
  )
  const workspaceListQueryKey = useMemo(
    () => ['WorkspacList', { memberID: memberId }] as const,
    [memberId]
  )
  return useMutation({
    mutationFn: async (input: CreateWorkspaceInput) => {
      try {
        const response = await gqlClient().request(updateWorkspaceMutation, {
          input,
        })
        return response
      } catch (e) {
        throwStandardError(e)
      }
    },
    onSuccess(_data, variables) {
      // Update dashboard workspace in the cache
      queryClient.setQueryData<WorkspaceDashboardQuery>(
        dashboardWorkspaceQueryKey,
        (currentData: WorkspaceDashboardQuery) => {
          const updatedWorkspaceData = {
            ...currentData.dashboard.workspace,
            ...(typeof variables.workspaceName !== 'undefined'
              ? { commonName: variables.workspaceName }
              : {}),
            ...(typeof variables.parentId !== 'undefined'
              ? { parentId: variables.parentId }
              : {}),
            ...(typeof variables.setupData?.billingCode !== 'undefined'
              ? { billingCode: variables.setupData?.billingCode }
              : {}),
          }

          return set(`dashboard.workspace`, updatedWorkspaceData, currentData)
        }
      )

      // Update member workspace list in the cache
      queryClient.setQueryData<WorkspacListQuery>(
        workspaceListQueryKey,
        (currentData: WorkspacListQuery) => {
          const workspaceIndex =
            currentData?.getWorkspacesForMember?.findIndex(
              (workspace) => workspace.id === variables.id
            ) ?? -1

          if (workspaceIndex === -1) {
            return currentData
          }
          const updatedWorkspace = {
            ...currentData?.getWorkspacesForMember[workspaceIndex],
            ...(typeof variables.workspaceName !== 'undefined'
              ? { commonName: variables.workspaceName }
              : {}),
            ...(typeof variables.parentId !== 'undefined'
              ? { parentId: variables.parentId }
              : {}),
            ...(typeof variables.setupData?.billingCode !== 'undefined'
              ? { billingCode: variables.setupData?.billingCode }
              : {}),
          }

          return set(
            `getWorkspacesForMember[${workspaceIndex}]`,
            updatedWorkspace,
            currentData
          )
        }
      )
    },
  })
}
